import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initAnalytics } from "./lib/analytics";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "development",
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

initAnalytics();

// After a deploy, old lazy-loaded chunk URLs (e.g. Dashboard-QkoxIBZg.js) no
// longer exist on the server. Reload to pick up the new bundle.
// Two entry points cover both failure modes:
//   vite:preloadError  — modulepreload link fails (Vite 5 synthetic event)
//   unhandledrejection — dynamic import() fails inside React.lazy() before
//                        React's error boundary can intercept it
function reloadOnStaleChunk(msg: string) {
  const key = "ftk_chunk_reload_at";
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 30_000) return;
  const lower = msg.toLowerCase();
  if (
    lower.includes("failed to fetch dynamically imported module") ||
    lower.includes("error loading dynamically imported module") ||
    lower.includes("importing a module script failed") ||
    lower.includes("chunkloaderror")
  ) {
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  }
}

window.addEventListener("vite:preloadError", () => {
  sessionStorage.setItem("ftk_chunk_reload_at", String(Date.now()));
  window.location.reload();
});

window.addEventListener("unhandledrejection", (e) => {
  reloadOnStaleChunk(e.reason?.message ?? String(e.reason ?? ""));
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary>
    <App />
  </Sentry.ErrorBoundary>
);
